import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocInlineDate,
  DocActionButton,
  DocCustomRows,
  DocAddRowButton,
  useDocRows,
} from '../../components/document';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { useDocuments, useSaveDocument } from '../../api/hooks';
import { cx } from '../../lib/cx';
import styles from './SeznameniPage.module.css';

/**
 * Seznámení s BOZP — "prezenční listina". Převzato z původní šablony
 * (frontend/index.html, tab-seznameni):
 *   • hlavička: SEZNÁMENÍ S TRAUMATOLOGICKÝM PLÁNEM STAVBY A PLÁNEM BOZP
 *   • tabulka: # · Datum · Jméno a příjmení · Firma / pozice · Podpis
 */

interface UcastnikRow {
  id: string;
  datum: string;
  jmeno: string;
  firma: string;
  podpis: string;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function todayCz(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${d.getFullYear()}`;
}

function emptyRow(): UcastnikRow {
  // Datum řádku necháváme prázdné — do tisku se propíše jen to, co účastník
  // skutečně vyplní; prázdné řádky zůstanou volné pro ruční zápis na stavbě.
  return { id: newId('u'), datum: '', jmeno: '', firma: '', podpis: '' };
}

/** Minimální počet řádků listiny při tisku (vyplněné + prázdné pro rukopis). */
const MIN_PRINT_ROWS = 12;

/** Řádek se bere jako vyplněný, pokud není jen prázdná šablona. */
function isParticipantRowFilled(r: UcastnikRow): boolean {
  const sig = r.podpis.trim();
  const sigMeaningful = sig !== '' && sig.toLowerCase() !== '(podpis)';
  return r.jmeno.trim() !== '' || r.firma.trim() !== '' || sigMeaningful;
}

function formatPrintPodpis(podpis: string): string {
  const t = podpis.trim();
  if (!t || t.toLowerCase() === '(podpis)') return '';
  return t;
}

type PrintSlot = { kind: 'data'; row: UcastnikRow } | { kind: 'blank' };

function buildPrintSlots(rows: UcastnikRow[]): PrintSlot[] {
  const filled = rows.filter(isParticipantRowFilled);
  if (filled.length >= MIN_PRINT_ROWS) {
    return filled.map((row) => ({ kind: 'data' as const, row }));
  }
  const blanks = MIN_PRINT_ROWS - filled.length;
  const blankSlots: PrintSlot[] = Array.from({ length: blanks }, () => ({ kind: 'blank' as const }));
  return [...filled.map((row) => ({ kind: 'data' as const, row })), ...blankSlots];
}

export function SeznameniPage() {
  const { activeStavba, setActiveStavbaId } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  // Nejnovější existující záznam seznámení pro tuto stavbu načteme do formuláře.
  const { data: existing = [] } = useDocuments({
    doc_type: 'seznameni',
    stavba: activeStavba?.nazev,
  });
  const save = useSaveDocument();
  const toast = useToast();

  const [stavbaNazev, setStavbaNazev] = useState<string>('');
  /** Pro uložení stavby do adresáře (/api/stavby/) — doplní výběr ze seznamu. */
  const [stavbaAdresa, setStavbaAdresa] = useState<string>('');
  const [stavbaPovoleni, setStavbaPovoleni] = useState<string>('');
  const [zakazka, setZakazka] = useState<string>('');
  const [datum, setDatum] = useState<string>(todayCz());
  const [lektor, setLektor] = useState<string>('');
  const [ucastnici, setUcastnici] = useState<UcastnikRow[]>([emptyRow()]);
  // Skryté standardní řádky + vlastní řádky v hlavičce (sekce 01).
  const rows = useDocRows();

  // Po načtení aktivní stavby — sladění s globálním výběrem (sidebar) i po „Načíst stavbu“.
  useEffect(() => {
    if (!activeStavba) return;
    setStavbaNazev(activeStavba.nazev);
    if (activeStavba.zakazka_cislo) setZakazka(activeStavba.zakazka_cislo);
    setStavbaAdresa(activeStavba.adresa ?? '');
    setStavbaPovoleni(activeStavba.stavebni_povoleni ?? '');
  }, [activeStavba]);

  function applyStavbaPick(p: StavbaPickResult) {
    setStavbaNazev(p.nazev);
    setStavbaAdresa(p.adresa);
    setStavbaPovoleni(p.stavebni_povoleni);
    if (p.zakazka_cislo) setZakazka(p.zakazka_cislo);
    setActiveStavbaId(p.id);
  }

  useEffect(() => {
    const latest = existing[0];
    if (!latest) return;
    const data = (latest.data_json ?? {}) as Record<string, unknown>;
    if (typeof data.datum === 'string') setDatum(data.datum);
    if (typeof data.lektor === 'string') setLektor(data.lektor);
    if (typeof data.stavba === 'string') setStavbaNazev(data.stavba);
    if (typeof data.adresa === 'string') setStavbaAdresa(data.adresa);
    if (typeof data.stavebni_povoleni === 'string') setStavbaPovoleni(data.stavebni_povoleni);
    if (typeof data.zakazka === 'string') setZakazka(data.zakazka);
    rows.apply(data.radky);
    if (Array.isArray(data.ucastnici) && data.ucastnici.length > 0) {
      const rows = (data.ucastnici as Array<Record<string, unknown>>).map((r) => ({
        id: newId('u'),
        datum: String(r.datum ?? ''),
        jmeno: String(r.jmeno ?? ''),
        firma: String(r.firma ?? ''),
        podpis: String(r.podpis ?? ''),
      }));
      setUcastnici(rows);
    }
  }, [existing, rows.apply]);

  const addRow = () => setUcastnici((list) => [...list, emptyRow()]);
  const updateRow = (id: string, patch: Partial<UcastnikRow>) =>
    setUcastnici((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) =>
    setUcastnici((list) => (list.length > 1 ? list.filter((r) => r.id !== id) : list));

  async function handleSave() {
    const cleanRows = ucastnici.filter((r) => r.jmeno.trim() !== '');
    const radky = rows.snapshot();
    try {
    await save.mutateAsync({
      doc_type: 'seznameni',
      title: `Seznámení s BOZP — ${stavbaNazev || 'stavba'} (${datum})`,
      stavba: activeStavba?.nazev ?? stavbaNazev,
      data_json: {
        datum,
        lektor,
        stavba: stavbaNazev,
        adresa: stavbaAdresa,
        stavebni_povoleni: stavbaPovoleni,
        zakazka,
        radky,
        ucastnici: cleanRows.map((r) => ({
          datum: r.datum,
          jmeno: r.jmeno,
          firma: r.firma,
          podpis: r.podpis,
        })),
        ucastnikov: cleanRows.length,
        podepsano: cleanRows.filter((r) => r.podpis.trim() !== '').length,
      },
    });
      toast.success('Seznámení s BOZP uloženo do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  const filledCount = ucastnici.filter((r) => r.jmeno.trim() !== '').length;

  const printSlots = useMemo(() => buildPrintSlots(ucastnici), [ucastnici]);

  // Kolik prázdných slotů dorovnat v editační tabulce, aby bylo hned vidět,
  // kolik řádků listina v tisku bude mít (stejný strop jako u tisku).
  const screenBlanks = Math.max(0, MIN_PRINT_ROWS - ucastnici.length);

  return (
    <>
      <DocLayout
        breadcrumb={['BOZP', 'Seznámení']}
        title="Seznámení s BOZP"
        statusText={
          filledCount === 0
            ? 'Prázdný formulář'
            : `${filledCount} účastník${filledCount === 1 ? '' : filledCount < 5 ? 'ci' : 'ků'}`
        }
        status={filledCount === 0 ? 'draft' : 'done'}
        actions={
          <>
            <Button variant="secondary" leading="⎙" onClick={() => printToPdf('Seznameni_BOZP.pdf')}>
              PDF (A4)
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? 'Ukládám…' : save.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: '',
              name: '',
              address: '',
            }}
            tag="Seznámení — BOZP"
            title={
              <>
                Seznámení s traumatologickým plánem stavby a plánem BOZP
                <span style={{ display: 'block', fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  {stavbaNazev || '—'}
                </span>
              </>
            }
            meta="Pracovníci byli prokazatelně seznámeni s riziky a traumatologickým plánem stavby včetně plánu BOZP."
            footer={{
              left: `Stavba: ${stavbaNazev || '—'}`,
              center: '',
              right: zakazka ? `Zakázka: ${zakazka}` : '',
            }}
          >
            <DocBlock
              idx="01"
              title="Identifikace stavby"
              hiddenCount={rows.hiddenCount('s01')}
              onRestoreHidden={() => rows.restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    label="Stavby"
                    onPick={applyStavbaPick}
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu do adresáře staveb"
                    build={() => ({
                      nazev: stavbaNazev.trim(),
                      adresa: stavbaAdresa.trim(),
                      stavebni_povoleni: stavbaPovoleni.trim() || undefined,
                      zakazka_cislo: zakazka.trim() || undefined,
                      datum_zahajeni: '',
                    })}
                  />
                </>
              }
            >
              {!rows.isHidden('s01.stavba') && (
                <DocRow
                  k="Stavba"
                  value={stavbaNazev}
                  onChange={setStavbaNazev}
                  placeholder="Název stavby"
                  onRemove={() => rows.hide('s01.stavba')}
                />
              )}
              {!rows.isHidden('s01.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={stavbaAdresa}
                  onChange={setStavbaAdresa}
                  placeholder="Načte se ze seznamu staveb nebo doplňte ručně"
                  onRemove={() => rows.hide('s01.adresa')}
                />
              )}
              {!rows.isHidden('s01.povoleni') && (
                <DocRow
                  k="Stavební povolení"
                  mono
                  value={stavbaPovoleni}
                  onChange={setStavbaPovoleni}
                  placeholder="Číslo povolení (volitelné)"
                  onRemove={() => rows.hide('s01.povoleni')}
                />
              )}
              {!rows.isHidden('s01.zakazka') && (
                <DocRow
                  k="Zakázka"
                  mono
                  value={zakazka}
                  onChange={setZakazka}
                  placeholder="Z-2026/…"
                  onRemove={() => rows.hide('s01.zakazka')}
                />
              )}
              {!rows.isHidden('s01.datum') && (
                <DocRow
                  k="Datum seznámení"
                  mono
                  value={datum}
                  onChange={setDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={datum} onChange={setDatum} />}
                  onRemove={() => rows.hide('s01.datum')}
                />
              )}
              {!rows.isHidden('s01.lektor') && (
                <DocRow
                  k="Lektor / BOZP koordinátor"
                  value={lektor}
                  onChange={setLektor}
                  placeholder="Jméno a funkce osoby, která provedla seznámení"
                  onRemove={() => rows.hide('s01.lektor')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Prezenční listina"
              actions={
                <DocActionButton onClick={addRow} title="Přidat účastníka">
                  + Přidat účastníka
                </DocActionButton>
              }
            >
              <div className={styles.ucastScreen}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12.5,
                    color: 'var(--sd-doc-ink)',
                  }}
                >
                  <thead>
                    <tr>
                      <th style={thStyle('32px')}>#</th>
                      <th style={thStyle('110px')}>Datum</th>
                      <th style={thStyle()}>Jméno a příjmení</th>
                      <th style={thStyle()}>Firma / pozice</th>
                      <th style={thStyle('160px')}>Podpis</th>
                      <th style={{ ...thStyle('32px'), borderRight: 0 }} className="docNoPrint" />
                    </tr>
                  </thead>
                  <tbody>
                    {ucastnici.map((r, i) => (
                      <tr key={r.id}>
                        <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
                          {i + 1}
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={r.datum}
                            onChange={(e) => updateRow(r.id, { datum: e.target.value })}
                            style={cellInputStyle}
                            placeholder="dd. mm. rrrr"
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={r.jmeno}
                            onChange={(e) => updateRow(r.id, { jmeno: e.target.value })}
                            style={cellInputStyle}
                            placeholder="Jan Novák"
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={r.firma}
                            onChange={(e) => updateRow(r.id, { firma: e.target.value })}
                            style={cellInputStyle}
                            placeholder="Firma s.r.o. / zedník"
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={r.podpis}
                            onChange={(e) => updateRow(r.id, { podpis: e.target.value })}
                            style={cellInputStyle}
                            placeholder="(podpis)"
                          />
                        </td>
                        <td
                          style={{ ...tdStyle, borderRight: 0, textAlign: 'center' }}
                          className="docNoPrint"
                        >
                          <button
                            type="button"
                            onClick={() => removeRow(r.id)}
                            title="Odebrat účastníka"
                            aria-label="Odebrat účastníka"
                            style={{
                              width: 22,
                              height: 22,
                              border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
                              borderRadius: 4,
                              background: '#fff',
                              cursor: 'pointer',
                              color: 'var(--sd-doc-dim)',
                            }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                    {Array.from({ length: screenBlanks }).map((_, bi) => {
                      const n = ucastnici.length + bi + 1;
                      return (
                        <tr key={`screen-blank-${bi}`} aria-hidden>
                          <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
                            {n}
                          </td>
                          <td style={tdStyle}>
                            <span className={styles.cellBlank} />
                          </td>
                          <td style={tdStyle}>
                            <span className={styles.cellBlank} />
                          </td>
                          <td style={tdStyle}>
                            <span className={styles.cellBlank} />
                          </td>
                          <td style={tdStyle}>
                            <span className={styles.cellBlank} />
                          </td>
                          <td style={{ ...tdStyle, borderRight: 0 }} className="docNoPrint" />
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className={styles.ucastPrint} aria-hidden>
                <table className={styles.printTable}>
                  <thead>
                    <tr>
                      <th className={styles.printTh} style={{ width: 32 }}>
                        #
                      </th>
                      <th className={styles.printTh} style={{ width: 110 }}>
                        Datum
                      </th>
                      <th className={styles.printTh}>Jméno a příjmení</th>
                      <th className={styles.printTh}>Firma / pozice</th>
                      <th className={styles.printTh} style={{ width: 160 }}>
                        Podpis
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {printSlots.map((slot, i) => {
                      const n = i + 1;
                      if (slot.kind === 'blank') {
                        return (
                          <tr key={`print-blank-${n}`}>
                            <td className={cx(styles.printTd, styles.printNum)}>{n}</td>
                            <td className={styles.printTd}>
                              <span className={styles.printBlank} />
                            </td>
                            <td className={styles.printTd}>
                              <span className={styles.printBlank} />
                            </td>
                            <td className={styles.printTd}>
                              <span className={styles.printBlank} />
                            </td>
                            <td className={styles.printTd}>
                              <span className={styles.printBlank} />
                            </td>
                          </tr>
                        );
                      }
                      const r = slot.row;
                      const d = r.datum.trim();
                      const j = r.jmeno.trim();
                      const f = r.firma.trim();
                      const p = formatPrintPodpis(r.podpis);
                      return (
                        <tr key={`print-data-${r.id}`}>
                          <td className={cx(styles.printTd, styles.printNum)}>{n}</td>
                          <td className={styles.printTd}>{d || <span className={styles.printBlank} />}</td>
                          <td className={styles.printTd}>{j || <span className={styles.printBlank} />}</td>
                          <td className={styles.printTd}>{f || <span className={styles.printBlank} />}</td>
                          <td className={styles.printTd}>{p || <span className={styles.printBlank} />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </DocBlock>
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="seznameni"
        docTitle={`Seznámení s BOZP — ${stavbaNazev || 'stavba'}`}
        defaultSubject={`Seznámení s BOZP — ${stavbaNazev || 'stavba'} (${datum})`}
        meta={`Stavba: ${stavbaNazev || '—'}`}
      />
    </>
  );
}

const thStyle = (width?: string): React.CSSProperties => ({
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--sd-doc-dim)',
  padding: '6px 8px',
  borderBottom: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
  borderRight: '1px solid var(--sd-w-eee5d0, #eee5d0)',
  width,
  background: 'var(--sd-w-faf7ec, #faf7ec)',
});

const tdStyle: React.CSSProperties = {
  padding: '4px 6px',
  borderBottom: '1px solid var(--sd-w-eee5d0, #eee5d0)',
  borderRight: '1px solid var(--sd-w-eee5d0, #eee5d0)',
  fontSize: 12.5,
  height: 34,
  verticalAlign: 'middle',
};

const cellInputStyle: React.CSSProperties = {
  width: '100%',
  border: 0,
  background: 'transparent',
  fontSize: 12.5,
  fontFamily: 'inherit',
  color: 'var(--sd-doc-ink)',
  outline: 'none',
  padding: '2px 0',
};
